import React from 'react';
import { Link as FluentLink } from '@fluentui/react';

/** How many rows a home list shows before the rest go behind the "more" toggle. */
export const HOME_LIST_PREVIEW_COUNT = 5;

/**
 * Caps a home list at a short preview and hands back the toggle for the rest.
 *
 * These lists are a glance at the state of things rather than somewhere to read a long list, and four of
 * them share one row: a tenant with twenty integrations would otherwise push the whole page down. The
 * items themselves are already ordered so the ones worth seeing come first, so the preview is the useful
 * part of every list and the toggle is for the tail.
 */
export function useHomeListPreview<T>(items: T[], previewCount: number = HOME_LIST_PREVIEW_COUNT) {
    const [expanded, setExpanded] = React.useState(false);
    const visible = expanded ? items : items.slice(0, previewCount);
    return {
        visible,
        hiddenCount: items.length - visible.length,
        expanded,
        toggle: () => setExpanded((wasExpanded) => !wasExpanded),
    };
}

interface IHomeListMoreProps {
    hiddenCount: number;
    expanded: boolean;
    onToggle: () => void;
}

/** The toggle itself. Renders nothing when the list already fits, which is the common case. */
export const HomeListMore: React.FC<IHomeListMoreProps> = ({ hiddenCount, expanded, onToggle }) => {
    if (hiddenCount === 0 && !expanded) return null;
    return (
        <FluentLink className='home-list-more' onClick={onToggle}>
            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </FluentLink>
    );
}
